import { MenuAction } from '@ordr-as/umami-react/context-menu';
import { ContextMenuButton } from '@ordr-as/umami-react/context-menu-button';
import type { MenuItemVariantDto, OrderLineDto } from '../../../../../../generated-api';
import { useActiveSearchableSections, useMenuCourses } from '../../../../../context/MenuSectionsStore';
import { useLocation } from '../../../../../context/LocationContext';
import { findMenuItemByOrderLine } from '../../../../../utils/cart';
import QuantityButtons from './QuantityButtons';
import React, { useMemo, useState } from 'react';
import CartItemCommentDialog from './Comment/CartItemCommentDialog';
import EditItemModal from './Edit/EditItemModal';
import CompositeCommentModal from './Edit/CompositeCommentModal';
import EditCompositeItemModal from './Edit/EditCompositeItemModal';
import ChangeCourseDialog from './ChangeCourse/ChangeCourseDialog';
import CompositeChangeCourseDialog from './ChangeCourse/CompositeChangeCourseDialog';
import ItemDetailsModal from './Info/ItemDetailsModal';
import GiftCardPhoneNumberDialog from './GiftCardPhoneNumber/GiftCardPhoneNumberDialog';
import { useAllProducts } from '../../../../../hooks/useAllProducts';

const hasOptions = (item: MenuItemVariantDto) => item.optionGroups?.length > 0;

const componentsHaveOptions = (components: OrderLineDto[] | undefined, allProducts: MenuItemVariantDto[]): boolean => {
  if (!components) {
    return false;
  }
  return components.some((comp) => {
    const compItem = allProducts.find((p) => p.variantLinkId === comp.itemVariantId);
    if ((compItem?.optionGroups?.length ?? 0) > 0) {
      return true;
    }
    return componentsHaveOptions(comp.components, allProducts);
  });
};

interface CartItemButtonsProps {
  orderLine: OrderLineDto;
}

type ModalType =
  | 'courses'
  | 'compositeCourses'
  | 'comment'
  | 'compositeComment'
  | 'editPhoneNumber'
  | 'edit'
  | 'editComposite'
  | 'info'
  | undefined;

const CartItemButtons = (props: CartItemButtonsProps) => {
  const { orderLine } = props;
  const { location } = useLocation();
  const menuSections = useActiveSearchableSections(location.zoneId);
  const allProducts = useAllProducts();
  const courses = useMenuCourses();
  const hasCourses = courses.length > 0;
  const isGiftCard = orderLine.giftCardId !== undefined;
  const isComposite = orderLine.isCompositeProduct;
  const [openModal, setOpenModal] = useState<ModalType>(undefined);

  const item = useMemo(() => findMenuItemByOrderLine(orderLine, menuSections), [menuSections, orderLine]);

  if (!item) {
    return (
      <div className="flex mt-3 justify-between">
        <QuantityButtons orderLine={orderLine} />
      </div>
    );
  }

  const editAction = (() => {
    if (isGiftCard) {
      return { label: 'Edit', modal: 'editPhoneNumber' as const };
    }
    if (isComposite && (hasOptions(item) || componentsHaveOptions(orderLine.components, allProducts))) {
      return { label: 'Edit', modal: 'editComposite' as const };
    }
    if (!isComposite && hasOptions(item)) {
      return { label: 'Edit', modal: 'edit' as const };
    }
    return { label: 'Info', modal: 'info' as const };
  })();

  return (
    <div className="flex mt-3 justify-between">
      <QuantityButtons orderLine={orderLine} />
      <ContextMenuButton height={16} width={16} rounded className="p-[15px]" backdrop={false}>
        <MenuAction
          className="text-brand-black"
          onClick={() => setOpenModal(isComposite ? 'compositeComment' : 'comment')}
        >
          Kitchen note
        </MenuAction>
        {hasCourses && (
          <MenuAction
            className="text-brand-black"
            onClick={() => setOpenModal(isComposite ? 'compositeCourses' : 'courses')}
          >
            Move to course
          </MenuAction>
        )}
        <MenuAction className="text-brand-black" onClick={() => setOpenModal(editAction.modal)}>
          {editAction.label}
        </MenuAction>
      </ContextMenuButton>
      {openModal === 'courses' && <ChangeCourseDialog onClose={() => setOpenModal(undefined)} orderLine={orderLine} />}
      {openModal === 'compositeCourses' && (
        <CompositeChangeCourseDialog onClose={() => setOpenModal(undefined)} parentOrderLine={orderLine} />
      )}
      {openModal === 'comment' && (
        <CartItemCommentDialog orderLine={orderLine} onClose={() => setOpenModal(undefined)} />
      )}
      {openModal === 'editPhoneNumber' && (
        <GiftCardPhoneNumberDialog onClose={() => setOpenModal(undefined)} orderLine={orderLine} />
      )}
      {openModal === 'edit' && (
        <EditItemModal onModalClose={() => setOpenModal(undefined)} orderLine={orderLine} item={item} />
      )}
      {openModal === 'compositeComment' && (
        <CompositeCommentModal onModalClose={() => setOpenModal(undefined)} parentOrderLine={orderLine} />
      )}
      {openModal === 'editComposite' && (
        <EditCompositeItemModal
          onModalClose={() => setOpenModal(undefined)}
          parentOrderLine={orderLine}
          parentItem={item}
        />
      )}
      {openModal === 'info' && <ItemDetailsModal onClose={() => setOpenModal(undefined)} item={item} />}
    </div>
  );
};

export default React.memo(CartItemButtons);
